import {
  WESTERN_PLANETS,
  type AyanamsaId,
  type BirthInput,
  type ChartMeta,
  type ChartResult,
  type HouseSystem,
  type JyotishData,
  type Planet,
  type WesternData,
} from '@astrally/shared';
import { getDefaultAdapter, type EphemerisAdapter } from '../ephemeris/index.js';
import { zonedToUtc } from '../time/timezone.js';
import { ayanamsaValue, computePositions } from '../core/positions.js';
import { computeAngles, housesFor, houseOf } from '../core/houses.js';
import { nakshatraOf, signCount, signOf, signPosition } from '../core/zodiac.js';
import { computeStandardVargas } from '../jyotish/vargas.js';
import { computeVimshottari } from '../jyotish/vimshottari.js';
import { computeDrishti } from '../jyotish/drishti.js';
import { evaluateYogas } from '../jyotish/yogas.js';
import { computeAspects, partOfFortune, type ChartPoint } from '../western/aspects.js';
import { detectPatterns } from '../western/patterns.js';
import { scanWindow } from '../uncertainty/windowScan.js';

export const ENGINE_VERSION = 'astro-engine@0.1.0';

export interface ComputeOptions {
  houseSystem?: HouseSystem;
  ayanamsaId?: AyanamsaId;
  adapter?: EphemerisAdapter;
  /** Sampling resolution for window modes (minutes). */
  stepMinutes?: number;
  /** Dasha year length in days. */
  dashaYearDays?: number;
}

/** Sidereal-sign dependencies per yoga rule for window-mode gating. */
const YOGA_DEPENDENCIES: Record<string, Planet[]> = {
  gajakesari: ['moon', 'jupiter'],
  budhaditya: ['sun', 'mercury'],
  'chandra-mangala': ['moon', 'mars'],
  kemadruma: ['moon', 'mars', 'mercury', 'jupiter', 'venus', 'saturn'],
  'mangal-dosha-moon': ['moon', 'mars'],
  'kaal-sarpa': ['sun', 'moon', 'mars', 'mercury', 'jupiter', 'venus', 'saturn', 'rahu'],
};

function hhmm(h: number, m: number): string {
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function computeChart(input: BirthInput, opts: ComputeOptions = {}): ChartResult {
  const adapter = opts.adapter ?? getDefaultAdapter();
  const houseSystem = opts.houseSystem ?? 'placidus';
  const ayanamsaId = opts.ayanamsaId ?? 'lahiri';
  const dashaYearDays = opts.dashaYearDays ?? 365.25;

  const baseMeta = (timeConfidence: ChartMeta['timeConfidence'], ayanDeg: number): ChartMeta => ({
    engineVersion: ENGINE_VERSION,
    calculatedAtISO: new Date().toISOString(),
    ephemerisAdapter: adapter.id,
    zodiac: { tropical: true, sidereal: true },
    ayanamsa: { id: ayanamsaId, valueDeg: Number(ayanDeg.toFixed(6)) },
    nodeConvention: 'mean',
    houseSystem,
    timeConfidence,
    dashaYearDays,
  });

  const { date, place, timeInput } = input;

  if (timeInput.kind === 'exact') {
    const utc = zonedToUtc(
      { year: date.year, month: date.month, day: date.day, ...timeInput.time },
      place.timezone,
    );
    return computeExactAt(utc, input, {
      adapter,
      houseSystem,
      ayanamsaId,
      dashaYearDays,
      timeConfidence: 'exact',
    });
  }

  // Window modes -------------------------------------------------------------
  const window =
    timeInput.kind === 'approximate'
      ? timeInput.window
      : { start: { hour: 0, minute: 0 }, end: { hour: 23, minute: 59 } };

  const utcStart = zonedToUtc(
    { year: date.year, month: date.month, day: date.day, ...window.start },
    place.timezone,
  );
  let utcEnd = zonedToUtc(
    { year: date.year, month: date.month, day: date.day, ...window.end },
    place.timezone,
  );
  // Windows like 21:00-03:00 cross local midnight into the next civil day.
  if (utcEnd.getTime() <= utcStart.getTime()) {
    utcEnd = new Date(utcEnd.getTime() + 86400000);
  }

  const mode = timeInput.kind === 'approximate' ? 'approximate' : 'unknown';
  const report = scanWindow(
    {
      utcStart,
      utcEnd,
      localStartLabel: hhmm(window.start.hour, window.start.minute),
      localEndLabel: hhmm(window.end.hour, window.end.minute),
      latitude: place.lat,
      longitude: place.lon,
      mode,
      stepMinutes: opts.stepMinutes ?? (mode === 'unknown' ? 10 : 5),
      ayanamsaId,
    },
    adapter,
  );

  const midUtc = new Date((utcStart.getTime() + utcEnd.getTime()) / 2);
  const ayanDeg = ayanamsaValue(midUtc, adapter, ayanamsaId);

  const stableSignPlanets = new Set(
    report.stable
      .filter((f) => f.id.endsWith('.siderealSign'))
      .map((f) => f.id.split('.')[1] as Planet),
  );

  // Moon-based structures only when their inputs are stable in the window.
  const moonNakStable = report.stable.some((f) => f.id === 'planet.moon.nakshatra');
  const midPositions = report.midpointPositions;
  const moonMid = midPositions.find((p) => p.planet === 'moon')!;

  const jyotish: JyotishData = {
    vargas: [],
    drishti: [],
    yogas: [],
  };
  if (moonNakStable) {
    jyotish.vimshottari = computeVimshottari(moonMid.lonSidereal, midUtc, dashaYearDays, 3);
  }
  const yogaResults = evaluateYogas({ positions: midPositions }); // no lagna in window modes
  jyotish.yogas = yogaResults.filter((y) => {
    const deps = YOGA_DEPENDENCIES[y.id];
    if (!deps) return false;
    return deps.every((p) => stableSignPlanets.has(p));
  });

  const western: WesternData = { aspects: [], patterns: [] };

  return {
    meta: baseMeta(mode, ayanDeg),
    birth: { date, timeInput, place },
    jyotish,
    western,
    uncertainty: report,
  };
}

interface ExactContext {
  adapter: EphemerisAdapter;
  houseSystem: HouseSystem;
  ayanamsaId: AyanamsaId;
  dashaYearDays: number;
  timeConfidence: ChartMeta['timeConfidence'];
}

function computeExactAt(utc: Date, input: BirthInput, ctx: ExactContext): ChartResult {
  const { adapter, houseSystem, ayanamsaId, dashaYearDays } = ctx;
  const { date, place, timeInput } = input;

  const positions = computePositions(utc, adapter, ayanamsaId);
  const ayanDeg = ayanamsaValue(utc, adapter, ayanamsaId);
  const angles = computeAngles(utc, place.lat, place.lon, adapter, ayanDeg);
  const houses = housesFor(houseSystem, utc, place.lat, place.lon, adapter, angles);

  const housePlacements: Partial<Record<Planet, number>> = {};
  for (const p of positions) {
    if (WESTERN_PLANETS.includes(p.planet)) {
      housePlacements[p.planet] = houseOf(p.lonTropical, houses.cusps);
    }
  }

  const lagnaSign = signOf(angles.ascendantSidereal);
  const bhavas: Partial<Record<Planet, number>> = {};
  for (const p of positions) {
    bhavas[p.planet] = signCount(lagnaSign, p.sidereal.sign);
  }

  const moon = positions.find((p) => p.planet === 'moon')!;

  const jyotish: JyotishData = {
    lagna: signPosition(angles.ascendantSidereal),
    lagnaNakshatra: nakshatraOf(angles.ascendantSidereal),
    bhavas,
    vargas: computeStandardVargas(positions, angles.ascendantSidereal),
    vimshottari: computeVimshottari(moon.lonSidereal, utc, dashaYearDays, 3),
    drishti: computeDrishti(positions),
    yogas: evaluateYogas({ positions, lagnaSign }),
  };

  // Part of Fortune — a calculated point, so it needs the Sun, Moon and Asc.
  const sun = positions.find((p) => p.planet === 'sun')!;
  const fortune = partOfFortune(angles.ascendant, sun.lonTropical, moon.lonTropical);

  const aspectPoints: ChartPoint[] = [
    ...positions
      .filter((p) => WESTERN_PLANETS.includes(p.planet))
      .map((p) => ({ id: p.planet as ChartPoint['id'], lon: p.lonTropical })),
    { id: 'ascendant', lon: angles.ascendant },
    { id: 'mc', lon: angles.mc },
    { id: 'fortune', lon: fortune.lon },
  ];

  const western: WesternData = {
    angles,
    houses,
    housePlacements,
    fortune: {
      lon: fortune.lon,
      sect: fortune.sect,
      sign: signPosition(fortune.lon),
      house: houseOf(fortune.lon, houses.cusps),
    },
    aspects: computeAspects(aspectPoints),
    patterns: detectPatterns(positions),
  };

  return {
    meta: {
      engineVersion: ENGINE_VERSION,
      calculatedAtISO: new Date().toISOString(),
      ephemerisAdapter: adapter.id,
      zodiac: { tropical: true, sidereal: true },
      ayanamsa: { id: ayanamsaId, valueDeg: Number(ayanDeg.toFixed(6)) },
      nodeConvention: 'mean',
      houseSystem,
      timeConfidence: ctx.timeConfidence,
      dashaYearDays,
    },
    birth: { date, timeInput, place, utcISO: utc.toISOString() },
    positions,
    jyotish,
    western,
  };
}

/**
 * Reference chart for unknown-time users. Explicitly labelled — never
 * presented as the actual natal chart.
 */
export function computeReferenceChart(
  input: BirthInput,
  kind: 'sunrise' | 'noon',
  opts: ComputeOptions = {},
): ChartResult | null {
  const adapter = opts.adapter ?? getDefaultAdapter();
  const { date, place } = input;

  let utc: Date;
  if (kind === 'noon') {
    utc = zonedToUtc(
      { year: date.year, month: date.month, day: date.day, hour: 12, minute: 0 },
      place.timezone,
    );
  } else {
    const dayStartUtc = zonedToUtc(
      { year: date.year, month: date.month, day: date.day, hour: 0, minute: 0 },
      place.timezone,
    );
    const rise = adapter.sunrise(place.lat, place.lon, dayStartUtc);
    if (!rise) return null;
    utc = rise;
  }

  const chart = computeExactAt(
    utc,
    { ...input, timeInput: { kind: 'exact', time: { hour: 0, minute: 0 } } },
    {
      adapter,
      houseSystem: opts.houseSystem ?? 'placidus',
      ayanamsaId: opts.ayanamsaId ?? 'lahiri',
      dashaYearDays: opts.dashaYearDays ?? 365.25,
      timeConfidence: 'unknown',
    },
  );
  chart.birth.timeInput = { kind: 'unknown' };
  chart.reference = {
    kind,
    note:
      kind === 'sunrise'
        ? 'Reference chart calculated for local sunrise — not an exact natal chart.'
        : 'Reference chart calculated for local noon — not an exact natal chart.',
  };
  return chart;
}
